// Company logo utility using logo.dev API and Duffel airline logos
// Usage: getCompanyLogoUrl("Marriott") => "https://img.logo.dev/marriott.com?token=..."
// Usage: getAirlineLogoUrl("United Airlines") => Duffel SVG logo or logo.dev fallback

const LOGO_DEV_TOKEN = "pk_EQN-dl7dSoq5H4EJSzgTGg";

// Duffel airline logo base URL
// See: https://duffel.com/docs/api/airlines/schema
const DUFFEL_LOGO_BASE = "https://assets.duffel.com/img/airlines/for-light-background/full-color-logo";

// Map airline names to IATA codes for Duffel logo lookup
const AIRLINE_IATA_CODES: Record<string, string> = {
  // US Major Airlines
  "united": "UA",
  "united airlines": "UA",
  "american": "AA",
  "american airlines": "AA",
  "delta": "DL",
  "delta air lines": "DL",
  "southwest": "WN",
  "southwest airlines": "WN",
  "jetblue": "B6",
  "jetblue airways": "B6",
  "alaska": "AS",
  "alaska airlines": "AS",
  "spirit": "NK",
  "spirit airlines": "NK",
  "frontier": "F9",
  "frontier airlines": "F9",
  "hawaiian": "HA",
  "hawaiian airlines": "HA",
  "sun country": "SY",
  "sun country airlines": "SY",
  "allegiant": "G4",
  "allegiant air": "G4",
  "breeze": "MX",
  "breeze airways": "MX",
  "avelo": "XP",
  "avelo airlines": "XP",
  "jsx": "XE",
  "jsx air": "XE",

  // European Airlines
  "british airways": "BA",
  "lufthansa": "LH",
  "air france": "AF",
  "klm": "KL",
  "iberia": "IB",
  "swiss": "LX",
  "swiss international": "LX",
  "austrian": "OS",
  "austrian airlines": "OS",
  "brussels airlines": "SN",
  "scandinavian": "SK",
  "sas": "SK",
  "finnair": "AY",
  "aer lingus": "EI",
  "tap": "TP",
  "tap portugal": "TP",
  "tap air portugal": "TP",
  "norwegian": "DY",
  "norwegian air": "DY",
  "ryanair": "FR",
  "easyjet": "U2",
  "vueling": "VY",
  "eurowings": "EW",
  "icelandair": "FI",
  "lot": "LO",
  "lot polish": "LO",
  "aegean": "A3",
  "aegean airlines": "A3",
  "virgin atlantic": "VS",
  "turkish": "TK",
  "turkish airlines": "TK",

  // Middle East Airlines
  "emirates": "EK",
  "qatar": "QR",
  "qatar airways": "QR",
  "etihad": "EY",
  "etihad airways": "EY",
  "saudia": "SV",
  "saudi arabian airlines": "SV",
  "gulf air": "GF",
  "oman air": "WY",
  "royal jordanian": "RJ",
  "el al": "LY",

  // Asian Airlines
  "singapore airlines": "SQ",
  "cathay pacific": "CX",
  "cathay": "CX",
  "japan airlines": "JL",
  "jal": "JL",
  "ana": "NH",
  "all nippon airways": "NH",
  "all nippon": "NH",
  "korean air": "KE",
  "asiana": "OZ",
  "asiana airlines": "OZ",
  "china airlines": "CI",
  "china eastern": "MU",
  "china southern": "CZ",
  "air china": "CA",
  "eva air": "BR",
  "thai": "TG",
  "thai airways": "TG",
  "vietnam airlines": "VN",
  "garuda": "GA",
  "garuda indonesia": "GA",
  "philippine airlines": "PR",
  "malaysia airlines": "MH",
  "air india": "AI",
  "indigo": "6E",

  // Americas (non-US)
  "air canada": "AC",
  "westjet": "WS",
  "aeromexico": "AM",
  "latam": "LA",
  "latam airlines": "LA",
  "copa": "CM",
  "copa airlines": "CM",
  "avianca": "AV",
  "gol": "G3",
  "azul": "AD",

  // Oceania
  "qantas": "QF",
  "air new zealand": "NZ",
  "fiji airways": "FJ",
  "virgin australia": "VA",

  // Africa
  "south african": "SA",
  "south african airways": "SA",
  "ethiopian": "ET",
  "ethiopian airlines": "ET",
  "kenya airways": "KQ",
  "egyptair": "MS",
  "royal air maroc": "AT",
};

// Known company name to domain mappings
const COMPANY_DOMAINS: Record<string, string> = {
  // Hotels
  "marriott": "marriott.com",
  "marriott hotels": "marriott.com",
  "marriott international": "marriott.com",
  "hilton": "hilton.com",
  "hilton hotels": "hilton.com",
  "hilton worldwide": "hilton.com",
  "hyatt": "hyatt.com",
  "hyatt hotels": "hyatt.com",
  "ihg": "ihg.com",
  "intercontinental": "ihg.com",
  "holiday inn": "ihg.com",
  "holiday inn express": "ihg.com",
  "crowne plaza": "ihg.com",
  "best western": "bestwestern.com",
  "wyndham": "wyndham.com",
  "radisson": "radissonhotels.com",
  "choice hotels": "choicehotels.com",
  "comfort inn": "choicehotels.com",
  "quality inn": "choicehotels.com",
  "four seasons": "fourseasons.com",
  "ritz carlton": "ritzcarlton.com",
  "ritz-carlton": "ritzcarlton.com",
  "the ritz-carlton": "ritzcarlton.com",
  "sheraton": "marriott.com",
  "westin": "marriott.com",
  "w hotels": "marriott.com",
  "st regis": "marriott.com",
  "st. regis": "marriott.com",
  "courtyard": "marriott.com",
  "courtyard by marriott": "marriott.com",
  "fairfield": "marriott.com",
  "fairfield inn": "marriott.com",
  "residence inn": "marriott.com",
  "springhill suites": "marriott.com",
  "hampton inn": "hilton.com",
  "hampton": "hilton.com",
  "doubletree": "hilton.com",
  "embassy suites": "hilton.com",
  "homewood suites": "hilton.com",
  "conrad": "hilton.com",
  "waldorf astoria": "hilton.com",
  "la quinta": "wyndham.com",
  "days inn": "wyndham.com",
  "super 8": "wyndham.com",
  "ramada": "wyndham.com",
  "accor": "accor.com",
  "novotel": "accor.com",
  "ibis": "accor.com",
  "sofitel": "accor.com",
  "fairmont": "fairmont.com",
  "omni": "omnihotels.com",
  "omni hotels": "omnihotels.com",
  "kimpton": "ihg.com",
  "loews": "loewshotels.com",
  "loews hotels": "loewshotels.com",
  "mgm": "mgmresorts.com",
  "mgm resorts": "mgmresorts.com",
  "mgm grand": "mgmresorts.com",
  "caesars": "caesars.com",
  "mandarin oriental": "mandarinoriental.com",
  "peninsula": "peninsula.com",
  "rosewood": "rosewoodhotels.com",
  "aman": "aman.com",
  "six senses": "sixsenses.com",
  "airbnb": "airbnb.com",
  "vrbo": "vrbo.com",

  // Cruise Lines
  "carnival": "carnival.com",
  "carnival cruise": "carnival.com",
  "carnival cruise line": "carnival.com",
  "royal caribbean": "royalcaribbean.com",
  "royal caribbean international": "royalcaribbean.com",
  "norwegian": "ncl.com",
  "norwegian cruise line": "ncl.com",
  "ncl": "ncl.com",
  "princess": "princess.com",
  "princess cruises": "princess.com",
  "celebrity": "celebritycruises.com",
  "celebrity cruises": "celebritycruises.com",
  "holland america": "hollandamerica.com",
  "holland america line": "hollandamerica.com",
  "msc": "msccruises.com",
  "msc cruises": "msccruises.com",
  "disney cruise": "disneycruise.disney.go.com",
  "disney cruise line": "disneycruise.disney.go.com",
  "viking": "vikingcruises.com",
  "viking cruises": "vikingcruises.com",
  "viking ocean cruises": "vikingcruises.com",
  "cunard": "cunard.com",
  "azamara": "azamara.com",
  "seabourn": "seabourn.com",
  "silversea": "silversea.com",
  "regent": "rssc.com",
  "regent seven seas": "rssc.com",
  "oceania": "oceaniacruises.com",
  "oceania cruises": "oceaniacruises.com",
  "windstar": "windstarcruises.com",
  "windstar cruises": "windstarcruises.com",
  "costa": "costacruises.com",
  "costa cruises": "costacruises.com",
  "virgin voyages": "virginvoyages.com",
  "explora journeys": "explorajourneys.com",

  // Car Rental Companies
  "hertz": "hertz.com",
  "enterprise": "enterprise.com",
  "enterprise rent-a-car": "enterprise.com",
  "avis": "avis.com",
  "budget": "budget.com",
  "national": "nationalcar.com",
  "national car rental": "nationalcar.com",
  "alamo": "alamo.com",
  "dollar": "dollar.com",
  "thrifty": "thrifty.com",
  "sixt": "sixt.com",
  "europcar": "europcar.com",
  "turo": "turo.com",
  "zipcar": "zipcar.com",
  "payless": "paylesscar.com",
  "fox": "foxrentacar.com",
  "fox rent a car": "foxrentacar.com",
  "ace": "acerentacar.com",
  "ace rent a car": "acerentacar.com",

  // Airlines (backup for those not in flight enrichment)
  "united": "united.com",
  "united airlines": "united.com",
  "american": "aa.com",
  "american airlines": "aa.com",
  "delta": "delta.com",
  "delta air lines": "delta.com",
  "southwest": "southwest.com",
  "southwest airlines": "southwest.com",
  "jetblue": "jetblue.com",
  "alaska": "alaskaair.com",
  "alaska airlines": "alaskaair.com",
  "spirit": "spirit.com",
  "spirit airlines": "spirit.com",
  "frontier": "flyfrontier.com",
  "frontier airlines": "flyfrontier.com",
  "british airways": "britishairways.com",
  "lufthansa": "lufthansa.com",
  "air france": "airfrance.com",
  "klm": "klm.com",
  "emirates": "emirates.com",
  "qatar": "qatarairways.com",
  "qatar airways": "qatarairways.com",
  "singapore airlines": "singaporeair.com",
  "cathay pacific": "cathaypacific.com",
  "japan airlines": "jal.com",
  "jal": "jal.com",
  "ana": "ana.co.jp",
  "all nippon airways": "ana.co.jp",
  "air canada": "aircanada.com",
  "qantas": "qantas.com",
  "virgin atlantic": "virginatlantic.com",
  "jsx": "jsx.com",
  "jsx air": "jsx.com",
  "etihad": "etihad.com",
  "etihad airways": "etihad.com",
  "turkish": "turkishairlines.com",
  "turkish airlines": "turkishairlines.com",
  "hawaiian": "hawaiianairlines.com",
  "hawaiian airlines": "hawaiianairlines.com",
  "sun country": "suncountry.com",
  "sun country airlines": "suncountry.com",
  "allegiant": "allegiantair.com",
  "allegiant air": "allegiantair.com",
  "breeze": "flybreeze.com",
  "breeze airways": "flybreeze.com",
  "avelo": "aveloair.com",
  "avelo airlines": "aveloair.com",
  "icelandair": "icelandair.com",
  "aeromexico": "aeromexico.com",
  "latam": "latam.com",
  "latam airlines": "latam.com",
  "copa": "copaair.com",
  "copa airlines": "copaair.com",
  "iberia": "iberia.com",
  "swiss": "swiss.com",
  "swiss international": "swiss.com",
  "austrian": "austrian.com",
  "austrian airlines": "austrian.com",
  "brussels airlines": "brusselsairlines.com",
  "scandinavian": "flysas.com",
  "sas": "flysas.com",
  "finnair": "finnair.com",
  "aer lingus": "aerlingus.com",
  "tap": "flytap.com",
  "tap portugal": "flytap.com",
  "korean air": "koreanair.com",
  "asiana": "flyasiana.com",
  "asiana airlines": "flyasiana.com",
  "china airlines": "china-airlines.com",
  "eva air": "evaair.com",
  "thai": "thaiairways.com",
  "thai airways": "thaiairways.com",
  "vietnam airlines": "vietnamairlines.com",
  "garuda": "garuda-indonesia.com",
  "garuda indonesia": "garuda-indonesia.com",
  "philippine airlines": "philippineairlines.com",
  "air new zealand": "airnewzealand.com",
  "fiji airways": "fijiairways.com",
  "south african": "flysaa.com",
  "south african airways": "flysaa.com",
  "ethiopian": "ethiopianairlines.com",
  "ethiopian airlines": "ethiopianairlines.com",
  "royal jordanian": "rj.com",
  "saudia": "saudia.com",
  "saudi arabian airlines": "saudia.com",
  "gulf air": "gulfair.com",
  "oman air": "omanair.com",
  "el al": "elal.com",

  // Ground Transportation / Car Services
  "blacklane": "blacklane.com",
  "uber": "uber.com",
  "lyft": "lyft.com",
  "supershuttle": "supershuttle.com",
  "super shuttle": "supershuttle.com",
  "groundlink": "groundlink.com",
  "carmel": "carmellimo.com",
  "carmel limo": "carmellimo.com",
  "limos.com": "limos.com",
  "execucar": "supershuttle.com",
  "go airlink": "goairlinkshuttle.com",
  "go airlink shuttle": "goairlinkshuttle.com",
  "prime time shuttle": "primetimeshuttle.com",

  // Activity/Tour Companies
  "viator": "viator.com",
  "getyourguide": "getyourguide.com",
  "get your guide": "getyourguide.com",
  "klook": "klook.com",
  "tripadvisor": "tripadvisor.com",
  "expedia": "expedia.com",
  "airbnb experiences": "airbnb.com",
  "disney": "disney.com",
  "walt disney world": "disneyworld.disney.go.com",
  "universal": "universalstudioshollywood.com",
  "universal studios": "universalstudioshollywood.com",
  "seaworld": "seaworld.com",
  "legoland": "legoland.com",
};

/**
 * Get the logo.dev URL for a company
 * @param companyName - The name of the company (e.g., "Marriott", "Carnival Cruise Line")
 * @param size - Logo size in pixels (default: 64)
 * @returns Logo URL or null if no domain found
 */
export function getCompanyLogoUrl(companyName: string, size: number = 64): string | null {
  if (!companyName) return null;

  const normalizedName = companyName.toLowerCase().trim();

  // First, try exact match in our mapping
  if (COMPANY_DOMAINS[normalizedName]) {
    return `https://img.logo.dev/${COMPANY_DOMAINS[normalizedName]}?token=${LOGO_DEV_TOKEN}&size=${size}&format=png`;
  }

  // Try partial match - check if any key is contained in the company name
  for (const [key, domain] of Object.entries(COMPANY_DOMAINS)) {
    if (normalizedName.includes(key) || key.includes(normalizedName)) {
      return `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=${size}&format=png`;
    }
  }

  // Fallback: try to guess the domain by cleaning the company name
  const guessedDomain = guessDomain(companyName);
  if (guessedDomain) {
    return `https://img.logo.dev/${guessedDomain}?token=${LOGO_DEV_TOKEN}&size=${size}&format=png`;
  }

  return null;
}

/**
 * Guess a domain from a company name
 */
function guessDomain(companyName: string): string | null {
  // Remove common suffixes and clean up the name
  const cleaned = companyName
    .toLowerCase()
    .replace(/\s*(hotels?|resorts?|cruise(s|line)?|rental?|car|inn|suites?|international|worldwide|inc\.?|corp\.?|llc|ltd\.?)\s*/gi, "")
    .replace(/[^a-z0-9]/g, "")
    .trim();

  if (cleaned.length < 2) return null;

  return `${cleaned}.com`;
}

/**
 * Get domain for a company (for use in other contexts)
 */
export function getCompanyDomain(companyName: string): string | null {
  if (!companyName) return null;

  const normalizedName = companyName.toLowerCase().trim();

  if (COMPANY_DOMAINS[normalizedName]) {
    return COMPANY_DOMAINS[normalizedName];
  }

  for (const [key, domain] of Object.entries(COMPANY_DOMAINS)) {
    if (normalizedName.includes(key) || key.includes(normalizedName)) {
      return domain;
    }
  }

  return guessDomain(companyName);
}

/**
 * Get IATA code for an airline
 */
export function getAirlineIataCode(airlineName: string): string | null {
  if (!airlineName) return null;

  const normalizedName = airlineName.toLowerCase().trim();

  // Direct match
  if (AIRLINE_IATA_CODES[normalizedName]) {
    return AIRLINE_IATA_CODES[normalizedName];
  }

  // Partial match - check if normalized name contains or is contained by any key
  for (const [key, code] of Object.entries(AIRLINE_IATA_CODES)) {
    if (normalizedName.includes(key) || key.includes(normalizedName)) {
      return code;
    }
  }

  // Try to extract IATA code from flight number format (e.g., "UA 123" -> "UA")
  const flightNumMatch = normalizedName.match(/^([a-z]{2})\s*\d/i);
  if (flightNumMatch) {
    return flightNumMatch[1].toUpperCase();
  }

  return null;
}

/**
 * Get Duffel airline logo URL (SVG) using IATA code
 * Returns null if IATA code is not found
 */
export function getDuffelAirlineLogoUrl(airlineName: string): string | null {
  const iataCode = getAirlineIataCode(airlineName);
  if (!iataCode) return null;

  return `${DUFFEL_LOGO_BASE}/${iataCode}.svg`;
}

/**
 * Get airline logo URL - tries Duffel first (high-quality SVG), falls back to logo.dev
 * @param airlineName - The name of the airline (e.g., "United Airlines", "Delta")
 * @param fallbackSize - Size for logo.dev fallback (default: 64)
 * @returns Object with url and source, or null if not found
 */
export function getAirlineLogoUrl(
  airlineName: string,
  fallbackSize: number = 64
): { url: string; source: 'duffel' | 'logodev' } | null {
  if (!airlineName) return null;

  // First try Duffel (high-quality SVG logos)
  const duffelUrl = getDuffelAirlineLogoUrl(airlineName);
  if (duffelUrl) {
    return { url: duffelUrl, source: 'duffel' };
  }

  // Fall back to logo.dev
  const logoDevUrl = getCompanyLogoUrl(airlineName, fallbackSize);
  if (logoDevUrl) {
    return { url: logoDevUrl, source: 'logodev' };
  }

  return null;
}

/**
 * Get airline logo URL string (for simple use cases that just need the URL)
 * Tries Duffel first, falls back to logo.dev
 * @param airlineName - The name of the airline
 * @param fallbackSize - Size for logo.dev fallback (default: 64)
 * @returns Logo URL string or null
 */
export function getAirlineLogoUrlString(
  airlineName: string,
  fallbackSize: number = 64
): string | null {
  const result = getAirlineLogoUrl(airlineName, fallbackSize);
  return result?.url ?? null;
}
